"use client";
import type { IconProps as PhosphorIconProps } from "@phosphor-icons/react";
import * as PhosphorIcons from "@phosphor-icons/react";
import { memo } from "react";
import { twMerge } from "tailwind-merge";

// Type for any Phosphor icon component
type PhosphorIconComponent = typeof PhosphorIcons.House;

// Extract all icon names from the Phosphor package
export type PhosphorIconName = keyof typeof PhosphorIcons;

// For backwards compatibility, export IconName as an alias
export type IconName = PhosphorIconName;

// Standard size presets for the design system
const sizePresets = {
  xs: 16,
  sm: 20,
  md: 24,
  lg: 32,
  xl: 40,
  "2xl": 48,
} as const;

type SizePreset = keyof typeof sizePresets;

interface IconProps extends Omit<PhosphorIconProps, "size"> {
  /**
   * The name of the Phosphor icon to render
   * Any valid icon from @phosphor-icons/react
   */
  name: PhosphorIconName;

  /**
   * Size of the icon - can be a preset or custom number
   */
  size?: SizePreset | number;

  /**
   * Accessibility label (maps to aria-label)
   */
  ariaLabel?: string;
}

/**
 * Icon
 *
 * Phosphor icon wrapper component with standardized sizing and styling.
 * Provides access to the full Phosphor icon library with tree-shaking support and design system presets.
 * - TypeScript autocomplete for all icon names
 *
 * Usage:
 * ```tsx
 * <Icon name="House" size="md" />
 * <Icon name="User" size={32} className="text-brand" />
 * ```
 */
export const Icon = memo(
  ({ name, size = "md", color, weight = "regular", className, ariaLabel, ...props }: IconProps) => {
    // Get the icon component from the Phosphor package
    const IconComponent = PhosphorIcons[name] as PhosphorIconComponent | undefined;

    if (!IconComponent) {
      if (process.env.NODE_ENV === "development") {
        console.error(`Icon "${name}" not found in @phosphor-icons/react`);
      }
      return null;
    }

    // Resolve size to pixel value
    const resolvedSize = typeof size === "string" && size in sizePresets ? sizePresets[size as SizePreset] : size;

    // Build className with design system defaults
    const iconClassName = twMerge(
      // Default styles
      "inline-block flex-shrink-0",
      // Allow color inheritance from parent
      !color && "text-current",
      // Custom classes
      className
    );

    return (
      <IconComponent
        {...props}
        size={resolvedSize as number}
        color={color}
        weight={weight}
        className={iconClassName}
        aria-label={ariaLabel || `${name} icon`}
        data-testid={`icon-${name}`}
      />
    );
  }
);

Icon.displayName = "Icon";

/**
 * Helper component for icons with containers
 */
interface IconWithContainerProps extends IconProps {
  /**
   * Container variant
   */
  variant?: "subtle" | "solid" | "outline";

  /**
   * Container shape
   */
  shape?: "square" | "circle";

  /**
   * Container size (adds padding around icon)
   */
  containerSize?: SizePreset | number;

  /**
   * Container background color
   */
  containerClassName?: string;
}

export const IconWithContainer = memo(
  ({
    variant = "subtle",
    shape = "square",
    containerSize,
    containerClassName,
    size = "md",
    className,
    name,
    color,
    weight,
    ariaLabel,
    ref,
    ...props
  }: IconWithContainerProps & { ref?: React.Ref<HTMLDivElement> }) => {
    // Calculate container size if not specified
    const iconSize = typeof size === "string" && size in sizePresets ? sizePresets[size as SizePreset] : size;
    const resolvedContainerSize = containerSize
      ? typeof containerSize === "string" && containerSize in sizePresets
        ? sizePresets[containerSize as SizePreset]
        : containerSize
      : (iconSize as number) * 1.75; // Default to 1.75x icon size

    const containerClasses = twMerge(
      // Base styles
      "inline-flex items-center justify-center flex-shrink-0",

      // Shape
      shape === "circle" ? "rounded-full" : "rounded-lg",

      // Variant styles using Tailwind 4 CSS variables
      variant === "subtle" && "bg-gray-100 dark:bg-gray-800",
      variant === "solid" && "bg-brand text-white",
      variant === "outline" && "border-2 border-current",

      // Container className for overrides
      containerClassName
    );

    return (
      <div
        className={containerClasses}
        style={{
          width: resolvedContainerSize,
          height: resolvedContainerSize,
        }}
      >
        <Icon
          {...props}
          name={name}
          size={size}
          color={color}
          weight={weight}
          className={className}
          ariaLabel={ariaLabel}
        />
      </div>
    );
  }
);

IconWithContainer.displayName = "IconWithContainer";
